import { List, Map, Record as ImmutableRecord, type RecordOf } from 'immutable';
import { isEmpty } from 'lodash';

import type { BackendMatches, MatchesState, RecordOfMatchesState } from './matches/types';
import type { ObjectsState } from './objects/types';

import type { RecordOfBettingEntitiesState, SSRBettingEntitiesState } from './types/bettingRootReducer';

// ---

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyType = any;

type TransformNodeImmutableEntity<T> = { byId: Map<number, T>; allIds?: List<number> };

// Betting node's server-side rendered state transform helper.
// Turns an object into immutable
export function transformNodeSSRState<
  SSREntity,
  ByIdImmutableRecord extends RecordOf<Record<string, AnyType>>,
  ImmutableEntity extends TransformNodeImmutableEntity<ByIdImmutableRecord>,
>(
  state: { byId: Record<string, SSREntity>; allIds?: number[] },
  transformRecord: ImmutableRecord.Factory<ImmutableEntity>,
  transformEntity: (value: SSREntity) => ByIdImmutableRecord,
): RecordOf<ImmutableEntity> {
  const byId = Map(
    Object
      .keys(state.byId)
      .map((key) => [parseInt(key), transformEntity(state.byId[key])]),
  );

  const node = state.allIds ? {
    byId,
    allIds: List(state.allIds),
  } : { byId };

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return transformRecord(node);
}

// ---

type Entity = RecordOf<AnyType>;
type State = Map<number, Entity>;
type KeyType = 'sportId' | 'tournamentUnionId' | 'tournamentId' | 'tournamentStageId';

// Removes matches from the state, if there are no matches left, then removes the state element
//
// clientEntities: { removeMatches: [3] }
// state -> { 1: { matchIds: [3, 4], matchesCount: 2 }, 2: { matchIds: [3], matchesCount: 1 } } }
// new state -> { 1: { matchIds: [4], matchesCount: 1 } }
export function removeMatches(
  key: KeyType,
  removedMatches: number[],
  matchesState: RecordOfMatchesState['byId'],
  state: State,
  stateIds?: List<number>,
): AnyType {
  removedMatches.forEach((matchId) => {
    const match = matchesState.get(matchId);

    if (match) {
      const stateItem = state.get(match[key]);

      if (stateItem && stateItem.matchIds.has(match.id)) {
        if (stateItem.matchesCount > 1) {
          const entity = state.get(match[key]);
          if (!entity) {
            console.error('lib > betting-entities > reducers > utils > removeMatches: `entity` not found');
            return state;
          }
          const matchIds = entity.get('matchIds');
          const matchesCount = entity.get('matchesCount');
          if (!matchIds || typeof matchesCount !== 'number') {
            return state;
          }
          matchIds.delete(match.id);
          state = state.setIn([match[key], 'matchIds'], new Set(matchIds));
          state = state.setIn([match[key], 'matchesCount'], matchesCount - 1);
        }
        else {
          state = state.delete(stateItem.id);

          if (stateIds) {
            const index = stateIds.indexOf(stateItem.id);

            if (index !== -1) {
              stateIds = stateIds.delete(index);
            }
          }
        }
      }
    }
  });

  return stateIds ? {
    state,
    stateIds,
  } : state;
}

// Fills a state item with match keys that belong to it
// After deletes state items in which there are no matches
//
// clientEntities: { matches: { 5: { id: 5, sportId: 1 }, 6: { id: 6, sportId: 1 } } }
// state -> { 1: { matchIds: [], matchesCount: 0 }, 2: { matchIds: [], matchesCount: 0 } }
// new state -> { 1: { matchIds: [5, 6], matchesCount: 2 } }
export function fillStateWithMatchIds(
  key: KeyType,
  matches: BackendMatches | undefined,
  matchesCount: { [key: string]: number } | undefined,
  state: State,
  stateIds?: List<number>,
): AnyType {
  const itemsWithoutMatches: number[] = [];

  const matchIds: { [sportId: string]: number[] } = {};

  matches && Object.keys(matches).forEach((matchKey) => {
    const match = matches[matchKey];

    if (match?.id) {
      const id = match[key];

      if (id) {
        if (matchIds[id]) {
          matchIds[id].push(match.id);
        }
        else {
          matchIds[id] = [match.id];
        }
      }
    }
  });

  state = state.map((stateItem) => {
    if (matchesCount) {
      stateItem = stateItem.update('matchesCount', () => (
        stateItem.id ? matchesCount[stateItem.id] : 0
      ));
    }
    else {
      const collection = matchIds[stateItem.id];

      if (collection) {
        stateItem = stateItem.update('matchIds', (ids) => (
          new Set([...ids, ...collection])
        ));

        stateItem = stateItem.update('matchesCount', () => (
          stateItem.matchIds.size
        ));
      }
    }

    if (stateItem.matchesCount === undefined || stateItem.matchesCount <= 0) {
      itemsWithoutMatches.push(stateItem.id);
    }

    return stateItem;
  });

  if (!isEmpty(itemsWithoutMatches)) {
    itemsWithoutMatches.forEach((id) => {
      state = state.delete(id);

      if (stateIds) {
        const index = stateIds.indexOf(id);

        if (index !== -1) {
          stateIds = stateIds.delete(index);
        }
      }
    });
  }

  return stateIds ? {
    state,
    stateIds,
  } : state;
}

// Updates a field in a state item
//
// clientEntities: { sports: { 1: { id: 1, name: 'test5' } } }
// state -> { 1: { name: 'test' } }
// new state -> { 1: { name: 'test5' } }
export function updateField(
  state: AnyType,
  path: number | (number | string)[],
  newValueKey: string,
  newValue: AnyType,
): AnyType {
  if (Array.isArray(path)) {
    return state.updateIn(path, (stateItem: AnyType) => (
      stateItem.update(newValueKey, () => newValue)
    ));
  }

  return state.update(path, (stateItem: AnyType) => (
    stateItem.update(newValueKey, () => newValue)
  ));
}

export function getErrorMessage(
  switchCase: string,
  state?: RecordOfBettingEntitiesState | SSRBettingEntitiesState,
) {
  return `BettingEntities -> ${switchCase} -> 'state' must be an Immutable.Record, current state = ${state}`;
}

export function isPrematchMatchesTopic(topic?: string) {
  return topic?.includes('pre-match-matches');
}

export function getPayloadMatchIdFromTopic(topic: string) {
  return parseInt(topic.split('#')[1]);
}

export function isMatchesByIdState(state: unknown): state is MatchesState['byId'] {
  return state instanceof Map;
}

export function isMatchesAllIdsState(state: unknown): state is MatchesState['allIds'] {
  return state instanceof List;
}

export function isMatchesObjectsState(state: unknown): state is ObjectsState['byId'] {
  return state instanceof Map;
}

export function updatePrematchMatches(state: RecordOfBettingEntitiesState, payloadMatchId: number) {
  state = state.updateIn(['matches', 'byId'], (matchesById) => {
    return isMatchesByIdState(matchesById) && matchesById?.filter((matchId) => {
      return matchId.get('id') !== payloadMatchId;
    });
  });

  state = state.updateIn(['matches', 'allIds'], (allIds) => {
    return isMatchesAllIdsState(allIds) && allIds?.filter((matchId) => {
      return matchId !== payloadMatchId;
    });
  });

  state = state.updateIn(['objects', 'byId'], (objectsById) => {
    return isMatchesObjectsState(objectsById) && objectsById?.filter((object) => {
      return !(object?.get('matchIds').has(payloadMatchId));
    });
  });

  return state;
}
